import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function PropertiesManagement() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    market: '',
    region: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const data = await api.get('/properties');
      setProperties(data);
    } catch (err) {
      console.error('Failed to load properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (property = null) => {
    if (property) {
      setEditingProperty(property);
      setFormData({
        name: property.name,
        market: property.market,
        region: property.region,
      });
    } else {
      setEditingProperty(null);
      setFormData({
        name: '',
        market: '',
        region: '',
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProperty(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingProperty) {
        await api.put(`/properties/${editingProperty.id}`, formData);
      } else {
        await api.post('/properties', formData);
      }
      await loadProperties();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;

    try {
      await api.delete(`/properties/${propertyId}`);
      await loadProperties();
    } catch (err) {
      alert('Failed to delete property: ' + err.message);
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Properties Management</h1>
        <p className="page-subtitle">Manage all Redstone Residential properties</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">All Properties</h2>
          <button className="btn btn-primary" onClick={() => handleOpenModal()} data-testid="create-property-btn">
            + Create Property
          </button>
        </div>

        {properties.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏢</div>
            <h3 className="empty-state-title">No Properties Yet</h3>
            <p className="empty-state-description">Get started by adding your first property.</p>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>Create Property</button>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Property Name</th>
                  <th>Market</th>
                  <th>Region</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((property) => (
                  <tr key={property.id}>
                    <td style={{ fontWeight: 500 }}>{property.name}</td>
                    <td>{property.market}</td>
                    <td>{property.region}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleOpenModal(property)}
                          data-testid={`edit-property-${property.id}`}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-small"
                          onClick={() => handleDelete(property.id)}
                          data-testid={`delete-property-${property.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingProperty ? 'Edit Property' : 'Create New Property'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}
                
                <div className="form-group">
                  <label className="form-label" htmlFor="name">Property Name</label>
                  <input
                    id="name"
                    type="text"
                    className="form-input"
                    placeholder="e.g., The Pines at Cedar Valley"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="property-name-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="market">Market</label>
                  <input
                    id="market"
                    type="text"
                    className="form-input"
                    placeholder="e.g., Utah County"
                    value={formData.market}
                    onChange={(e) => setFormData({ ...formData, market: e.target.value })}
                    required
                    data-testid="property-market-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="region">Region</label>
                  <input
                    id="region"
                    type="text"
                    className="form-input"
                    placeholder="e.g., Central"
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    required
                    data-testid="property-region-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal} data-testid="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="save-property-btn">
                  {editingProperty ? 'Save Changes' : 'Create Property'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertiesManagement;
